"use client";

import { useRef, useState } from "react";
import { SymbolChart } from "@/components/tool-kit/cipher-workbench";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  GlyphIcon,
  KEY_FIGURES,
  PigpenKey,
  PigpenMessageSvg,
} from "@/components/tools/cipher-machines/pigpen-drawing";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { ALPHABET } from "@/lib/tool-kit/ciphers/alphabet";
import { pigpenLayout } from "@/lib/tool-kit/ciphers/pigpen";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";

type Task = "encode" | "decode";

const PER_LINE_OPTIONS = ["8", "12", "16", "24"] as const;
type PerLine = (typeof PER_LINE_OPTIONS)[number];

/** Where an X's four letters sit in a 3 × 3 button layout: top, left, right, bottom. */
const X_BUTTON_CELLS = [1, 3, 5, 7];

/** English strings. A translated content file overrides these through `ui`. */
export const PIGPEN_CIPHER_STRINGS = {
  task: "What do you want to do?",
  taskEncode: "Write text in pigpen",
  taskDecode: "Read pigpen symbols",
  message: "Your message",
  messagePlaceholder: "Type the text to write in pigpen",
  sample: "X marks the spot",
  perLine: "Symbols per line",
  result: "Your message in pigpen",
  imageLabel: "Pigpen symbols for: {text}",
  noLetters: "Type some letters to draw them in pigpen.",
  skipped:
    "one: {chars} has no pigpen symbol, so it was left out.|other: {chars} have no pigpen symbols, so they were left out.",
  letterCount: "one: {count} symbol|other: {count} symbols",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  exportFailed:
    "The PNG could not be made in this browser. Download the SVG instead.",
  fileName: "pigpen-message",
  keyboard: "Select the symbols in the order they are written",
  keyboardHint:
    "The buttons are laid out like the key: the plain grid, the dotted grid, the plain X and the dotted X.",
  symbolFor: "Pigpen symbol for {letter}",
  space: "Space",
  backspace: "Delete last",
  clearDecoded: "Clear",
  decoded: "Decoded text",
  decodedEmpty: "Selected letters appear here.",
  copy: "Copy text",
  copied: "Copied!",
  keyHeading: "The pigpen key",
  keyLabel:
    "Pigpen key: letters A to I in a grid, J to R in a grid with dots, S to V in an X, W to Z in an X with dots.",
  keyHint:
    "A letter's symbol is the shape of the lines around it in the key. The second grid and the second X add a dot.",
  chartLabel: "The pigpen symbol for every letter",
} as const;

type Strings = typeof PIGPEN_CIPHER_STRINGS;

function Encoder({ strings }: { strings: Strings }) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState<string>(strings.sample);
  const [perLine, setPerLine] = useState<PerLine>("12");
  const [failed, setFailed] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const width = Number(perLine);
  const layout = pigpenLayout(text, width);
  const longest = Math.max(1, ...layout.lines.map((line) => line.length));

  async function savePng() {
    if (!svgRef.current) {
      return;
    }
    try {
      setFailed(false);
      await downloadPng(`${strings.fileName}.png`, svgRef.current, {
        scale: 2,
      });
    } catch {
      setFailed(true);
    }
  }

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          id="pp-message"
          label={strings.message}
          onChange={setText}
          placeholder={strings.messagePlaceholder}
          prose
          value={text}
        />
        <div className="max-w-xs">
          <SelectField
            id="pp-per-line"
            label={strings.perLine}
            onChange={setPerLine}
            options={PER_LINE_OPTIONS.map((value) => ({
              value,
              label: fmt(Number(value)),
            }))}
            value={perLine}
          />
        </div>
        {layout.skipped.length > 0 ? (
          <Notice>
            {tPlural(strings.skipped, locale, layout.skipped.length, {
              chars: layout.skipped.join(" "),
            })}
          </Notice>
        ) : null}
      </section>
      <section aria-live="polite" className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.result}</h2>
        {layout.letters === 0 ? (
          <Notice>{strings.noLetters}</Notice>
        ) : (
          <>
            <p className={HINT}>
              {tPlural(strings.letterCount, locale, layout.letters, {
                count: fmt(layout.letters),
              })}
            </p>
            <div className="overflow-hidden rounded-lg border border-border bg-white">
              <PigpenMessageSvg
                label={t(strings.imageLabel, { text: text.trim() })}
                lines={layout.lines}
                perLine={Math.min(width, longest)}
                svgRef={svgRef}
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  svgRef.current &&
                  downloadSvg(`${strings.fileName}.svg`, svgRef.current)
                }
                type="button"
              >
                {strings.downloadSvg}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={savePng}
                type="button"
              >
                {strings.downloadPng}
              </button>
            </div>
            {failed ? <ErrorNotice>{strings.exportFailed}</ErrorNotice> : null}
          </>
        )}
      </section>
    </>
  );
}

function SymbolButton({
  letter,
  onPress,
  strings,
}: {
  letter: string;
  onPress: (letter: string) => void;
  strings: Strings;
}) {
  return (
    <button
      aria-label={t(strings.symbolFor, { letter })}
      className="flex h-14 w-14 flex-col items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:border-primary hover:bg-primary/5"
      onClick={() => onPress(letter)}
      type="button"
    >
      <GlyphIcon letter={letter} size={30} />
      <span className="text-[10px] text-muted-foreground leading-none">
        {letter}
      </span>
    </button>
  );
}

function Decoder({ strings }: { strings: Strings }) {
  const [decoded, setDecoded] = useState("");
  const press = (letter: string) => setDecoded((current) => current + letter);
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <p className={LABEL}>{strings.keyboard}</p>
        <p className={HINT}>{strings.keyboardHint}</p>
      </div>
      <div className="flex flex-wrap gap-6">
        {KEY_FIGURES.map((figure) => {
          const cells: (string | null)[] =
            figure.kind === "grid"
              ? [...figure.letters]
              : Array.from({ length: 9 }, (_, cell) => {
                  const slot = X_BUTTON_CELLS.indexOf(cell);
                  return slot >= 0 ? figure.letters.charAt(slot) : null;
                });
          return (
            <div className="grid grid-cols-3 gap-1.5" key={figure.letters}>
              {cells.map((letter, cell) =>
                letter ? (
                  <SymbolButton
                    key={letter}
                    letter={letter}
                    onPress={press}
                    strings={strings}
                  />
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: empty layout cells are positional
                  <span aria-hidden="true" className="h-14 w-14" key={cell} />
                )
              )}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => press(" ")}
          type="button"
        >
          {strings.space}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={decoded === ""}
          onClick={() => setDecoded((current) => current.slice(0, -1))}
          type="button"
        >
          {strings.backspace}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={decoded === ""}
          onClick={() => setDecoded("")}
          type="button"
        >
          {strings.clearDecoded}
        </button>
      </div>
      <div aria-live="polite" className="space-y-2">
        <label className={LABEL} htmlFor="pp-decoded">
          {strings.decoded}
        </label>
        <output
          className="block min-h-12 whitespace-pre-wrap break-all rounded-lg border border-border bg-card p-4 font-mono text-foreground text-lg tracking-wide"
          id="pp-decoded"
        >
          {decoded === "" ? (
            <span className="text-muted-foreground text-sm">
              {strings.decodedEmpty}
            </span>
          ) : (
            decoded
          )}
        </output>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={decoded}
        />
      </div>
    </section>
  );
}

export default function PigpenCipherWidget() {
  const strings = useToolUi(PIGPEN_CIPHER_STRINGS);
  const [task, setTask] = useState<Task>("encode");
  return (
    <>
      <PillGroup
        label={strings.task}
        name="pp-task"
        onChange={setTask}
        options={[
          { value: "encode", label: strings.taskEncode },
          { value: "decode", label: strings.taskDecode },
        ]}
        value={task}
      />
      {task === "encode" ? (
        <Encoder strings={strings} />
      ) : (
        <Decoder strings={strings} />
      )}
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.keyHeading}</h2>
        <PigpenKey label={strings.keyLabel} />
        <p className={HINT}>{strings.keyHint}</p>
        <SymbolChart
          items={[...ALPHABET].map((letter) => ({
            key: letter,
            value: <GlyphIcon letter={letter} size={28} />,
          }))}
          label={strings.chartLabel}
        />
      </section>
    </>
  );
}
